import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { civilLawContracts, employees, departmentsApi } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

interface Form {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  contract_subject: string;
  date_from: string;
  date_to: string;
  total_amount: string;
  calc_type: 'ONCE' | 'DAILY' | 'HOURLY';
  department_id: string;
  comment: string;
}

const today = new Date();
const EMPTY: Form = {
  organization_id: '',
  document_number: '',
  document_date: today.toISOString().slice(0, 16),
  employee_id: '',
  contract_subject: '',
  date_from: today.toISOString().slice(0, 10),
  date_to: '',
  total_amount: '0',
  calc_type: 'ONCE',
  department_id: '',
  comment: '',
};

export default function CivilLawContractEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const empFromUrl = search.get('employee');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, hasPermissionInAnyOrg, loading: permsLoading } =
    usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['civil-law-contract', id],
    queryFn: () => civilLawContracts.get(id!),
    enabled: !isNew,
  });
  const [form, setForm] = useState<Form>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  // Лише працівники виду CIVIL_LAW (договір ЦПУ — для ГПХ).
  const empListQ = useQuery({
    queryKey: ['employees-civil', form.organization_id],
    queryFn: () => employees.list({ organization_id: form.organization_id, limit: 1000 }),
    enabled: !!form.organization_id,
  });
  const civilEmps = (empListQ.data?.items ?? []).filter(
    (e) => e.employment_type === 'CIVIL_LAW',
  );
  // Підрозділи обраної юрособи (для поля «Підрозділ»).
  const depQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        employee_id: empFromUrl ?? f.employee_id,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        employee_id: d.employee_id,
        contract_subject: d.contract_subject ?? '',
        date_from: d.date_from,
        date_to: d.date_to ?? '',
        total_amount: String(d.total_amount),
        calc_type: (d.calc_type as 'ONCE' | 'DAILY' | 'HOURLY') ?? 'ONCE',
        department_id: d.department_id ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs, empFromUrl]);

  const state = docQuery.data?.state ?? 'DRAFT';
  const isPosted = !isNew && state === 'POSTED';
  const isCancelled = !isNew && state === 'CANCELLED';

  const create = useMutation({
    mutationFn: () => civilLawContracts.create({
      organization_id: form.organization_id,
      document_number: form.document_number || undefined,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      contract_subject: form.contract_subject || null,
      date_from: form.date_from,
      date_to: form.date_to || null,
      total_amount: form.total_amount,
      calc_type: form.calc_type,
      department_id: form.department_id || null,
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['civil-law-contracts'] });
      navigate(`/civil-law-contracts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () => civilLawContracts.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contract_subject: form.contract_subject || null,
      date_from: form.date_from,
      date_to: form.date_to || null,
      total_amount: form.total_amount,
      calc_type: form.calc_type,
      department_id: form.department_id || null,
      comment: form.comment || null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['civil-law-contract', id] }),
    onError: (e) => setError(extractError(e)),
  });

  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await civilLawContracts.create({
        organization_id: form.organization_id,
        document_number: form.document_number || undefined,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        contract_subject: form.contract_subject || null,
        date_from: form.date_from,
        date_to: form.date_to || null,
        total_amount: form.total_amount,
      calc_type: form.calc_type,
        department_id: form.department_id || null,
        comment: form.comment || null,
      });
      try {
        await civilLawContracts.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/civil-law-contracts/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['civil-law-contracts'] });
      navigate(`/civil-law-contracts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await civilLawContracts.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        contract_subject: form.contract_subject || null,
        date_from: form.date_from,
        date_to: form.date_to || null,
        total_amount: form.total_amount,
      calc_type: form.calc_type,
        department_id: form.department_id || null,
        comment: form.comment || null,
      });
      await civilLawContracts.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['civil-law-contract', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => civilLawContracts.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['civil-law-contract', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => civilLawContracts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['civil-law-contracts'] });
      navigate('/employees');
    },
  });

  function validate(): boolean {
    if (!form.organization_id) { setError('Оберіть організацію'); return false; }
    if (!form.employee_id) { setError('Оберіть працівника (вид ЦПУ)'); return false; }
    if (!form.date_from) { setError('Вкажіть дату початку'); return false; }
    setError(null);
    return true;
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  function onSaveAndPost() {
    if (!validate()) return;
    createAndPost.mutate();
  }
  function onUpdateAndPost() {
    if (!validate()) return;
    updateAndPost.mutate();
  }

  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{
          padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
          border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
          maxWidth: 600, margin: '1rem auto', textAlign: 'center' }}>
          ⚠ У вас немає прав створювати договори ЦПУ у жодній організації.
        </div>
      </div>
    );
  }

  // ч.878 (новий дизайн, Закон #10): «ще дій» не було. Прогрес — обовʼязкові:
  // організація, працівник, дата початку.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 1, filled: form.organization_id ? 1 : 0 },
    { id: 'terms', title: 'Виконавець і умови договору', required: 2,
      filled: (form.employee_id ? 1 : 0) + (form.date_from ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий договір ЦПУ' : docQuery.data?.document_number ?? '...'}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        {/* ч.641: канон DocumentToolbar (плашка дій + бейджі стану). */}
        <DocumentToolbar
          onBackToList={() => navigate('/employees')}
          onSave={onSave}
          saveDisabled={isPosted || isCancelled || create.isPending || update.isPending}
          savePending={create.isPending || update.isPending}
          isNew={isNew}
          isPosted={isPosted}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postDisabled={isCancelled || create.isPending || createAndPost.isPending || updateAndPost.isPending}
          postPending={createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={!isCancelled}
          onDelete={() => { confirm('Видалити договір ЦПУ?').then((ok) => { if (ok) remove.mutate(); }); }}
          entriesDoc={{ type: 'doc.civil_law_contracts', id: isNew ? '' : id! }}
          extraStatusBadges={isCancelled ? [{ label: 'скасовано', tone: 'red' }] : []}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {isCancelled && (
        <div style={{
          background: 'var(--badge-red-bg)', border: '1px solid #fecaca', color: 'var(--badge-red-fg)',
          borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.75rem' }}>
          🚫 Документ скасовано. {docQuery.data?.cancellation_reason}
        </div>
      )}

      {error && (
        <div style={{
          background: 'var(--badge-red-bg)', border: '1px solid #fecaca', color: 'var(--badge-red-fg)',
          borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.75rem' }}>{error}</div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.civil_law_contracts', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew || isPosted}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
            <option value="">—</option>
            {(orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Номер</label>
          <input value={form.document_number} placeholder="(авто)"
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date} disabled={isPosted}
                 onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Виконавець і умови договору">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Виконавець (ЦПУ) *</label>
          <select value={form.employee_id} disabled={!isNew || isPosted}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
            <option value="">—</option>
            {civilEmps.map((e) => (
              <option key={e.id} value={e.id}>{e.full_name ?? e.id}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата початку *</label>
          <DatePopover value={form.date_from} disabled={isPosted}
                 onChange={(v) => setForm({ ...form, date_from: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата завершення</label>
          <DatePopover value={form.date_to} disabled={isPosted}
                 onChange={(v) => setForm({ ...form, date_to: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Вид розрахунку</label>
          <select value={form.calc_type} disabled={isPosted}
                  onChange={(e) => setForm({ ...form, calc_type: e.target.value as 'ONCE' | 'DAILY' | 'HOURLY' })}>
            <option value="ONCE">разово</option>
            <option value="DAILY">подобово</option>
            <option value="HOURLY">погодинно</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Розмір, грн{form.calc_type === 'DAILY' ? ' (за день)'
            : form.calc_type === 'HOURLY' ? ' (за годину)' : ''}</label>
          <input type="number" step="0.01" value={form.total_amount} disabled={isPosted}
                 onChange={(e) => setForm({ ...form, total_amount: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Підрозділ</label>
          <select value={form.department_id} disabled={isPosted}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
            <option value="">— не вказано —</option>
            {(depQ.data?.items ?? []).map((d) => (
              <option key={d.id} value={d.id}>{d.name} — {d.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Предмет договору</label>
          <input value={form.contract_subject} disabled={isPosted}
                 onChange={(e) => setForm({ ...form, contract_subject: e.target.value })} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.civil_law_contracts'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
